import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Contestacao.css";
import { useAuth } from "../context/ApiKeyContext";
import { useHistory } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@material-ui/core";
import { Fullscreen, FullscreenExit, Info } from "@material-ui/icons";
import { readDossieCellRulesAsObservations } from "../utils/serviceCustomizationApi";
import { pdfjs } from "react-pdf";
import { getApiBaseUrl } from "../config";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const API_BASE_URL = getApiBaseUrl();
const PRODUCT_CODE_DOSSIE = "DC_CONTESTACAO_DOSSIE";

const ContestacaoDossieCreate = () => {
  const [formValid, setFormValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const { token, cell, user, hasRole } = useAuth();
  const history = useHistory();
  const { t } = useTranslation("jobs");

  const [fileData, setFileData] = useState({
    inicialFile: null,
    subsidiosFiles: [],
  });
  const [filePages, setFilePages] = useState({});

  const [formData, setFormData] = useState({
    local: "",
    date: "",
    empresaDefendida: "",
    jobName: "",
    observacoesGerais: "",
  });

  const [useDefaultParams, setUseDefaultParams] = useState(false);
  const [observations, setObservations] = useState({
    client: "",
    internal: "",
    subsidios: "",
  });

  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [loadDefaultsError, setLoadDefaultsError] = useState(false);
  const [defaultsRetryCountdown, setDefaultsRetryCountdown] = useState(0);
  const cancelDefaultsRetryRef = useRef(false);
  const defaultsRetryTimerRef = useRef(null);

  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenField, setFullscreenField] = useState(null);
  const [fullscreenValue, setFullscreenValue] = useState("");

  const safeObservations = {
    client: observations?.client || "",
    internal: observations?.internal || "",
    subsidios: observations?.subsidios || "",
  };

  const hasCustomizationPermission = () => {
    if (!user) return false;
    if (!user.permissions) return false;
    return hasRole(
      "DOCUMENT_CREATION",
      "edit_custom_specific",
      PRODUCT_CODE_DOSSIE,
    );
  };

  const handleClientObservationChange = useCallback((e) => {
    if (!e?.target) return;
    const value = e.target.value;
    setObservations((prev) => ({ ...prev, client: value }));
  }, []);

  const handleInternalObservationChange = useCallback((e) => {
    if (!e?.target) return;
    const value = e.target.value;
    setObservations((prev) => ({ ...prev, internal: value }));
  }, []);

  const handleSubsidiosObservationChange = useCallback((e) => {
    if (!e?.target) return;
    const value = e.target.value;
    setObservations((prev) => ({ ...prev, subsidios: value }));
  }, []);

  const handleExpandTextarea = (field) => {
    setFullscreenField(field);
    setFullscreenValue(safeObservations[field] || "");
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    if (fullscreenField) {
      setObservations((prev) => ({
        ...prev,
        [fullscreenField]: fullscreenValue,
      }));
    }
    setFullscreenOpen(false);
    setFullscreenField(null);
    setFullscreenValue("");
  };

  const handleUseDefaultParamsChange = useCallback((e) => {
    if (!e?.target) return;
    setUseDefaultParams(e.target.checked);
  }, []);

  const fetchDefaultValues = useCallback(async () => {
    const cellValue = cell?.value;
    if (!cellValue || !token) return;

    const obs = await readDossieCellRulesAsObservations(token, cellValue);
    const hasSuperUser = hasRole(
      "DOCUMENT_CREATION",
      "edit_custom_super_user",
      PRODUCT_CODE_DOSSIE,
    );

    setObservations({
      client: obs.client || "",
      subsidios: obs.subsidios || "",
      internal: hasSuperUser ? obs.internal || "" : "",
    });
  }, [cell?.value, token, hasRole]);

  const loadDefaultValues = useCallback(async () => {
    if (!cell?.value || !token || !hasCustomizationPermission()) return;

    cancelDefaultsRetryRef.current = false;
    setLoadingDefaults(true);
    setLoadDefaultsError(false);
    setDefaultsRetryCountdown(0);

    const attemptLoad = async () => {
      try {
        await fetchDefaultValues();
        setLoadDefaultsError(false);
        setLoadingDefaults(false);
      } catch (error) {
        console.error("Erro ao carregar valores padrão:", error);
        if (cancelDefaultsRetryRef.current) {
          setLoadingDefaults(false);
          return;
        }
        setLoadDefaultsError(true);
        for (let s = 3; s > 0; s--) {
          if (cancelDefaultsRetryRef.current) {
            setLoadingDefaults(false);
            return;
          }
          setDefaultsRetryCountdown(s);
          await new Promise((resolve) => {
            defaultsRetryTimerRef.current = setTimeout(resolve, 1000);
          });
        }
        if (!cancelDefaultsRetryRef.current) {
          setDefaultsRetryCountdown(0);
          setLoadDefaultsError(false);
          attemptLoad();
        } else {
          setLoadingDefaults(false);
        }
      }
    };

    attemptLoad();
  }, [cell?.value, token, fetchDefaultValues]);

  const handleCancelDefaultsLoad = useCallback(() => {
    cancelDefaultsRetryRef.current = true;
    if (defaultsRetryTimerRef.current) {
      clearTimeout(defaultsRetryTimerRef.current);
      defaultsRetryTimerRef.current = null;
    }
    setLoadingDefaults(false);
    setLoadDefaultsError(false);
    setDefaultsRetryCountdown(0);
  }, []);

  const validateForm = useCallback(() => {
    const { inicialFile, subsidiosFiles } = fileData;
    const { local, date, empresaDefendida, jobName } = formData;

    const subsidiosTrim = (observations?.subsidios || "").trim();
    const canCustomizeParams =
      Boolean(user?.permissions) &&
      hasRole(
        "DOCUMENT_CREATION",
        "edit_custom_specific",
        PRODUCT_CODE_DOSSIE,
      );
    const needsSubsidiosRules =
      canCustomizeParams &&
      useDefaultParams &&
      subsidiosFiles &&
      subsidiosFiles.length > 0;

    const isValid =
      inicialFile !== null &&
      local.trim() !== "" &&
      date !== "" &&
      empresaDefendida.trim() !== "" &&
      jobName.trim() !== "" &&
      (!needsSubsidiosRules || subsidiosTrim !== "");

    setFormValid(isValid);
  }, [fileData, formData, useDefaultParams, observations, user, hasRole]);

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  useEffect(() => {
    loadDefaultValues();
    return () => {
      cancelDefaultsRetryRef.current = true;
      if (defaultsRetryTimerRef.current) {
        clearTimeout(defaultsRetryTimerRef.current);
      }
    };
  }, [cell?.value, token, user]);

  const countPdfPages = async (file) => {
    if (!file || file.type !== "application/pdf") {
      return null;
    }
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch (err) {
      console.error("Erro ao contar páginas do PDF:", err);
      return null;
    }
  };

  const calculateTotalPages = () => {
    let total = 0;
    if (fileData.inicialFile && filePages[fileData.inicialFile.name]) {
      total += filePages[fileData.inicialFile.name];
    }
    fileData.subsidiosFiles.forEach((file) => {
      if (filePages[file.name]) {
        total += filePages[file.name];
      }
    });
    return total;
  };

  useEffect(() => {
    const filesContainer = document.getElementById("subsidios-list-dossie");
    if (!filesContainer) return;
    filesContainer.innerHTML = "";

    fileData.subsidiosFiles.forEach((file) => {
      const fileChip = document.createElement("div");
      fileChip.className = "file-chip";
      const pageCount = filePages[file.name];
      const pagesText =
        pageCount === 1
          ? t("contestacao.fileUpload.page")
          : t("contestacao.fileUpload.pages");

      const nameSpan = document.createElement("span");
      nameSpan.className = "file-chip-name";
      nameSpan.textContent = file.name;
      fileChip.appendChild(nameSpan);

      if (pageCount) {
        const pagesSpan = document.createElement("span");
        pagesSpan.className = "file-chip-pages text-success";
        pagesSpan.textContent = ` (${pageCount} ${pagesText})`;
        fileChip.appendChild(pagesSpan);
      }

      const removeSpan = document.createElement("span");
      removeSpan.className = "remove";
      removeSpan.textContent = "×";
      fileChip.appendChild(removeSpan);
      filesContainer.appendChild(fileChip);

      const removeButton = fileChip.querySelector(".remove");
      if (removeButton) {
        removeButton.addEventListener("click", function () {
          try {
            fileChip.remove();
            const remaining = Array.from(
              document.querySelectorAll("#subsidios-list-dossie .file-chip"),
            )
              .map((chip) => {
                const text = chip.textContent.replace("×", "").trim();
                return text
                  .replace(/\s*\(\d+\s+(página|páginas|page|pages)\)$/i, "")
                  .trim();
              })
              .filter((name) => name);

            setFileData((prev) => {
              const removedFiles = prev.subsidiosFiles.filter(
                (f) => !remaining.includes(f.name),
              );
              setFilePages((prevPages) => {
                const updated = { ...prevPages };
                removedFiles.forEach((f) => {
                  delete updated[f.name];
                });
                return updated;
              });
              return {
                ...prev,
                subsidiosFiles: prev.subsidiosFiles.filter((f) =>
                  remaining.includes(f.name),
                ),
              };
            });
          } catch (err) {
            console.error("Erro ao remover arquivo:", err);
          }
        });
      }
    });
  }, [fileData.subsidiosFiles, filePages, t]);

  const handleFileChange = async (e) => {
    if (!e?.target) return;
    const { id, files } = e.target;

    if (id === "subsidios-files-dossie") {
      const newFiles = Array.from(files);
      setFileData((prev) => {
        const existingFileNames = prev.subsidiosFiles.map((f) => f.name);
        const uniqueNewFiles = newFiles.filter(
          (file) => !existingFileNames.includes(file.name),
        );
        return {
          ...prev,
          subsidiosFiles: [...prev.subsidiosFiles, ...uniqueNewFiles],
        };
      });

      for (const file of newFiles) {
        if (file.type === "application/pdf") {
          const pageCount = await countPdfPages(file);
          if (pageCount !== null) {
            setFilePages((prev) => ({
              ...prev,
              [file.name]: pageCount,
            }));
          }
        }
      }
      e.target.value = "";
    } else if (id === "inicial-file-dossie") {
      const selectedFile = files.length > 0 ? files[0] : null;
      setFileData((prev) => ({
        ...prev,
        inicialFile: selectedFile,
      }));

      if (selectedFile && selectedFile.type === "application/pdf") {
        const pageCount = await countPdfPages(selectedFile);
        if (pageCount !== null) {
          setFilePages((prev) => ({
            ...prev,
            [selectedFile.name]: pageCount,
          }));
        }
      }
    }
  };

  const handleInputChange = (e) => {
    if (!e?.target) return;
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleProcessClick = async () => {
    if (!formValid) return;
    if (!token) {
      setError(t("common:authTokenNotFound"));
      return;
    }

    setError(null);
    setIsProcessing(true);

    try {
      const apiFormData = new FormData();
      apiFormData.append("peticao_inicial", fileData.inicialFile);
      fileData.subsidiosFiles.forEach((file) => {
        apiFormData.append("subsidios", file);
      });

      apiFormData.append("empresa_defendida", formData.empresaDefendida);
      apiFormData.append("job_name", formData.jobName);
      apiFormData.append("data_documento", formData.date);
      if (formData.observacoesGerais) {
        apiFormData.append("instrucoes_especificas", formData.observacoesGerais);
      }

      if (useDefaultParams) {
        const regrasDict = {};
        if (safeObservations.client) {
          regrasDict["RULES"] = safeObservations.client;
        }
        if (
          safeObservations.internal &&
          hasRole(
            "DOCUMENT_CREATION",
            "edit_custom_super_user",
            PRODUCT_CODE_DOSSIE,
          )
        ) {
          regrasDict["RULES_INTERNAL"] = safeObservations.internal;
        }
        if (safeObservations.subsidios) {
          regrasDict["RULES_SUBSIDIOS"] = safeObservations.subsidios;
        }
        if (Object.keys(regrasDict).length > 0) {
          apiFormData.append("regras", JSON.stringify(regrasDict));
        }
      }

      const cellId = cell?.value || "undefined";
      const url = `${API_BASE_URL}/doc_creator/cria_contestacao_dossie?cell_id=${cellId}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
          ServiceName: "DOCUMENT_CREATION",
        },
        body: apiFormData,
      });

      if (!response.ok) {
        let errorMessage = `${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === "string") {
            errorMessage = errorData;
          }
        } catch (parseError) {
          console.warn("Não foi possível parsear resposta de erro:", parseError);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data.job_id) {
        throw new Error(t("common:invalidResponse"));
      }

      setIsProcessing(false);
      history.push("/contestacao-dossie");
    } catch (err) {
      console.error("Erro ao processar documento:", err);
      setError(t("common:processingError", { error: err.message }));
      setIsProcessing(false);
    }
  };

  return (
    <div className="container contestacao-page">
      <header>
        <div className="flex-row-between mb-20">
          <div>
            <h1>{t("contestacaoDossieCreate.title")}</h1>
          </div>
          <button
            type="button"
            onClick={() => history.push("/contestacao-dossie")}
            className="ikaro-btn ikaro-btn-primary ikaro-btn-md"
          >
            {t("contestacaoDossieCreate.backToList")}
          </button>
        </div>
      </header>

      <div className="card">
        <h3 className="card-title">
          <i>📝</i> {t("contestacaoDossieCreate.taskCardTitle")}
        </h3>
        <div className="form-group">
          <label htmlFor="jobName" className="required">
            {t("contestacaoDossieCreate.taskNameLabel")}
          </label>
          <input
            type="text"
            id="jobName"
            placeholder={t("contestacaoDossieCreate.taskNamePlaceholder")}
            required
            value={formData.jobName}
            onChange={handleInputChange}
          />
          <div className="info-text">{t("contestacaoDossieCreate.taskNameHint")}</div>
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">
          <i>📄</i> {t("contestacaoDossieCreate.requiredFiles")}
        </h3>
        <div className="form-group">
          <div className="file-input">
            <label className="required">
              {t("contestacaoDossieCreate.initialPetition")}
            </label>
            <div className="file-upload">
              <div className="file-upload-label">
                <i>📎</i>
                <span className="file-name">
                  {fileData.inicialFile ? (
                    (() => {
                      const pageCount = filePages[fileData.inicialFile.name];
                      if (pageCount) {
                        const pagesText =
                          pageCount === 1
                            ? t("contestacao.fileUpload.page")
                            : t("contestacao.fileUpload.pages");
                        return (
                          <>
                            <span className="file-name-text">
                              {fileData.inicialFile.name}
                            </span>
                            <span className="file-name-pages text-success">
                              ({pageCount} {pagesText})
                            </span>
                          </>
                        );
                      }
                      return (
                        <span className="file-name-text">
                          {fileData.inicialFile.name}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="file-name-text">
                      {t("contestacaoDossieCreate.selectFile")}
                    </span>
                  )}
                </span>
              </div>
              <input
                type="file"
                id="inicial-file-dossie"
                accept=".pdf,.jpg,.jpeg,.png"
                required
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="info-text">
            {t("contestacaoDossieCreate.initialPetitionInfo")}
          </div>
        </div>

        <div className="form-group">
          <div className="file-input">
            <label className="flex-row gap-8">
              {t("contestacaoDossieCreate.subsidies")}
              <span className="tooltip-wrapper">
                <Info className="tooltip-icon" />
                <span className="tooltip-content">
                  {t("contestacaoDossieCreate.subsidiesInfo")}
                </span>
              </span>
            </label>
            <div className="file-upload">
              <div className="file-upload-label">
                <i>📎</i>
                <span className="file-name">
                  {fileData.subsidiosFiles.length > 0
                    ? `${fileData.subsidiosFiles.length} ${t(
                        "contestacaoDossieCreate.filesSelected",
                      )}`
                    : t("contestacaoDossieCreate.selectFiles")}
                </span>
              </div>
              <input
                type="file"
                id="subsidios-files-dossie"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp3,.wav,.m4a,.aac,.ogg,.oga,.opus,.webm,.flac,.wma,.amr,audio/*"
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="multiple-files" id="subsidios-list-dossie"></div>
          {(() => {
            const totalPages = calculateTotalPages();
            if (totalPages > 0) {
              const pagesText =
                totalPages === 1
                  ? t("contestacao.fileUpload.page")
                  : t("contestacao.fileUpload.pages");
              return (
                <div className="info-text mt-8 text-center font-medium text-success">
                  {t("contestacao.fileUpload.totalPages", {
                    total: totalPages,
                    pagesText,
                  })}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">
          <i>🏢</i> {t("contestacaoDossieCreate.companyInfo")}
        </h3>
        <div className="form-group">
          <label htmlFor="empresaDefendida" className="required">
            {t("contestacaoDossieCreate.defendedCompany")}
          </label>
          <input
            type="text"
            id="empresaDefendida"
            placeholder={t("contestacaoDossieCreate.defendedCompanyPlaceholder")}
            required
            value={formData.empresaDefendida}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="local" className="required">
            {t("contestacaoDossieCreate.location")}
          </label>
          <input
            type="text"
            id="local"
            placeholder={t("contestacaoDossieCreate.locationPlaceholder")}
            required
            value={formData.local}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="date" className="required">
            {t("contestacaoDossieCreate.documentDate")}
          </label>
          <input
            type="date"
            id="date"
            required
            value={formData.date}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">
          <i>⚙️</i> {t("contestacaoDossieCreate.parametersCardTitle")}
        </h3>
        <div className="form-group">
          <label htmlFor="observacoesGerais">
            {t("contestacaoDossieCreate.parameters.specificInstructions")}
          </label>
          <textarea
            id="observacoesGerais"
            placeholder={t(
              "contestacaoDossieCreate.parameters.specificInstructionsPlaceholder",
            )}
            value={formData.observacoesGerais}
            onChange={handleInputChange}
            rows="4"
          />
          <div className="info-text">
            {t("contestacaoDossieCreate.parameters.specificInstructionsInfo")}
          </div>
        </div>
      </div>

      {hasCustomizationPermission() && (
        <div className="card" style={{ position: "relative" }}>
          <h3 className="card-title">
            <i>⚙️</i> {t("contestacaoDossieCreate.advancedParamsTitle")}
          </h3>

          {loadingDefaults && (
            <div
              className="cell-loading-overlay"
              style={{ borderRadius: "8px" }}
            >
              <div className="cell-loading-content">
                <div className="cell-loading-spinner" />
                {loadDefaultsError ? (
                  <>
                    <Typography className="cell-loading-retry-text">
                      {t("contestacao.cellParams.loading.retrying", {
                        seconds: defaultsRetryCountdown,
                      })}
                    </Typography>
                    <button
                      type="button"
                      className="cell-loading-cancel-btn"
                      onClick={handleCancelDefaultsLoad}
                    >
                      {t("contestacao.cellParams.loading.cancel")}
                    </button>
                  </>
                ) : (
                  <Typography className="cell-loading-text">
                    {t("contestacao.cellParams.loading.loadingData")}
                  </Typography>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={useDefaultParams}
                onChange={handleUseDefaultParamsChange}
              />
              <span className="checkmark"></span>
              {t("contestacaoDossieCreate.parameters.useDefaultParams")}
            </label>
            <p className="info-text">
              {t("contestacaoDossieCreate.parameters.useDefaultParamsDescription")}
            </p>

            {useDefaultParams && (
              <div className="expanded-params">
                <div className="custom-rules-section">
                  <div className="form-group">
                    <label htmlFor="client-custom-rules-dossie">
                      {t("contestacaoDossieCreate.customRules.client")}
                    </label>
                    <div className="relative">
                      <textarea
                        id="client-custom-rules-dossie"
                        placeholder={t(
                          "contestacaoDossieCreate.customRules.clientPlaceholder",
                        )}
                        value={safeObservations.client}
                        onChange={handleClientObservationChange}
                        rows="4"
                      />
                      <button
                        type="button"
                        onClick={() => handleExpandTextarea("client")}
                        className="contestacao-fullscreen-expand-btn"
                        title={t("contestacaoDossieCreate.expandFullscreen")}
                      >
                        <Fullscreen className="contestacao-expand-icon" />
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subsidios-custom-rules-dossie">
                      {t("contestacaoDossieCreate.parameters.subsidiosRules")}
                    </label>
                    <div className="relative">
                      <textarea
                        id="subsidios-custom-rules-dossie"
                        placeholder={t(
                          "contestacaoDossieCreate.parameters.subsidiosRulesPlaceholder",
                        )}
                        value={safeObservations.subsidios}
                        onChange={handleSubsidiosObservationChange}
                        rows="4"
                      />
                      <button
                        type="button"
                        onClick={() => handleExpandTextarea("subsidios")}
                        className="contestacao-fullscreen-expand-btn"
                        title={t("contestacaoDossieCreate.expandFullscreen")}
                      >
                        <Fullscreen className="contestacao-expand-icon" />
                      </button>
                    </div>
                    <div className="info-text">
                      {fileData.subsidiosFiles.length > 0
                        ? t(
                            "contestacaoDossieCreate.parameters.subsidiosRulesInfoWithFiles",
                          )
                        : safeObservations.subsidios
                          ? t(
                              "contestacaoDossieCreate.parameters.subsidiosRulesInfoOverride",
                            )
                          : t(
                              "contestacaoDossieCreate.parameters.subsidiosRulesInfoDefault",
                            )}
                    </div>
                  </div>

                  {hasRole(
                    "DOCUMENT_CREATION",
                    "edit_custom_super_user",
                    PRODUCT_CODE_DOSSIE,
                  ) && (
                    <div className="form-group">
                      <label htmlFor="internal-custom-rules-dossie">
                        {t("contestacaoDossieCreate.customRules.internal")}
                      </label>
                      <div className="relative">
                        <textarea
                          id="internal-custom-rules-dossie"
                          placeholder={t(
                            "contestacaoDossieCreate.customRules.internalPlaceholder",
                          )}
                          value={safeObservations.internal}
                          onChange={handleInternalObservationChange}
                          rows="4"
                        />
                        <button
                          type="button"
                          onClick={() => handleExpandTextarea("internal")}
                          className="contestacao-fullscreen-expand-btn"
                          title={t("contestacaoDossieCreate.expandFullscreen")}
                        >
                          <Fullscreen className="contestacao-expand-icon" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="error-alert">
          <i className="error-icon">⚠️</i>
          <span>{error}</span>
        </div>
      )}

      <button
        id="process-btn-dossie"
        type="button"
        className="process-btn"
        disabled={!formValid || isProcessing}
        onClick={handleProcessClick}
      >
        {isProcessing
          ? t("contestacaoDossieCreate.processing")
          : t("contestacaoDossieCreate.generateDossier")}
      </button>

      <footer>
        <p>{t("contestacaoDossieCreate.footer")}</p>
      </footer>

      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        fullScreen
        PaperProps={{
          className: "contestacao-fullscreen-paper",
        }}
      >
        <DialogTitle className="contestacao-dialog-title">
          <Typography variant="h6">
            {fullscreenField === "client" &&
              t("contestacaoDossieCreate.customRules.client")}
            {fullscreenField === "internal" &&
              t("contestacaoDossieCreate.customRules.internal")}
            {fullscreenField === "subsidios" &&
              t("contestacaoDossieCreate.parameters.subsidiosRulesModalTitle")}
          </Typography>
          <Button
            onClick={handleCloseFullscreen}
            size="small"
            className="contestacao-dialog-close-btn"
          >
            <FullscreenExit />
          </Button>
        </DialogTitle>
        <DialogContent className="contestacao-dialog-content">
          <TextField
            fullWidth
            multiline
            value={fullscreenValue}
            onChange={(e) => setFullscreenValue(e.target.value)}
            variant="outlined"
            placeholder={
              fullscreenField === "client"
                ? t("contestacaoDossieCreate.customRules.clientPlaceholder")
                : fullscreenField === "internal"
                  ? t("contestacaoDossieCreate.customRules.internalPlaceholder")
                  : t(
                      "contestacaoDossieCreate.parameters.subsidiosRulesPlaceholder",
                    )
            }
            InputProps={{
              className: "contestacao-fullscreen-input",
            }}
            inputProps={{
              className: "contestacao-fullscreen-input-inner",
            }}
            className="flex-1"
          />
        </DialogContent>
        <DialogActions className="contestacao-dialog-actions">
          <Button
            onClick={handleCloseFullscreen}
            variant="contained"
            className="ikaro-btn-primary"
          >
            {t("common:close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContestacaoDossieCreate;
